import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { WhiteLabelTextReplacement } from '@api/model/white-label';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { generateObjectId } from '../../../../../util/generate-object-id';
import { takeUntil } from 'rxjs/operators';

type TextReplacementItem = Omit<WhiteLabelTextReplacement, 'files'> & {
  file: string;
};

@Component({
  selector: 'app-create-white-label-text-replacements',
  templateUrl: './create-white-label-text-replacements.component.html',
  styleUrls: ['./create-white-label-text-replacements.component.scss']
})
export class CreateWhiteLabelTextReplacementsComponent implements OnInit, OnDestroy {
  @Input() initialTextReplacements?: WhiteLabelTextReplacement[] | null = null;
  @Input() whitelabelDetailsFormValue: Record<string, string> | null = null;
  @Input() whiteLabelStylesFormValue: Record<string, string> | null = null;
  @Input() whiteLabelStoreDetailsFormValue: Record<string, string> | null = null;
  @Input() whiteLabelIntegrationFormValue: Record<string, string> | null = null;
  @Input() whiteLabelEmailConfigFormValue: Record<string, string> | null = null;

  @Output() changeTextReplacements = new EventEmitter<WhiteLabelTextReplacement[]>();

  form = new UntypedFormGroup({
    customTextReplacements: new UntypedFormArray([])
  });

  customTextReplacements = this.form.controls.customTextReplacements as UntypedFormArray;

  editCache: { rowId: string; data: TextReplacementItem } | null = null;

  private unsubscribe$ = new Subject();

  constructor() {
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(change => {
      this.emitTextReplacements();
    });
  }

  ngOnInit() {
    if (this.initialTextReplacements) {
      const editableTextReplacements = this.initialTextReplacements.filter(textReplacement => textReplacement.isEditable);

      editableTextReplacements.forEach(textReplacement => {
        textReplacement.files.forEach(file => {
          const id = generateObjectId();

          const formGroup = new UntypedFormGroup({
            id: new UntypedFormControl(id),
            file: new UntypedFormControl(file),
            from: new UntypedFormControl(textReplacement.from),
            to: new UntypedFormControl(textReplacement.to),
            isEditable: new UntypedFormControl(true)
          });

          this.customTextReplacements.push(formGroup);
        });
      });

      this.emitTextReplacements();
    }
  }

  private emitTextReplacements() {
    const textReplacements: WhiteLabelTextReplacement[] = [];

    this.customTextReplacements.controls.forEach(textReplacementFormGroup => {
      const { file, from, to } = textReplacementFormGroup.value;

      const textReplacementIndex = textReplacements.findIndex(
        customTextReplacement => customTextReplacement.from === from && customTextReplacement.to === to
      );

      if (textReplacementIndex >= 0) {
        const customTextReplacement = textReplacements[textReplacementIndex];

        if (!customTextReplacement.files.includes(file)) {
          customTextReplacement.files.push(file);
        }
      } else {
        textReplacements.push({ files: [file], from, to, isEditable: true });
      }
    });

    this.changeTextReplacements.emit(textReplacements);
  }

  startEdit(id: string, currentData: UntypedFormGroup): void {
    this.editCache = {
      rowId: id,
      data: currentData.value
    };
  }

  cancelEdit(): void {
    this.editCache = null;
  }

  saveEdit(currentData: UntypedFormGroup): void {
    currentData.patchValue(this.editCache!.data);
    this.cancelEdit();
  }

  addTextReplacement() {
    const id = generateObjectId();

    const formGroup = new UntypedFormGroup({
      id: new UntypedFormControl(id),
      file: new UntypedFormControl(''),
      from: new UntypedFormControl(''),
      to: new UntypedFormControl(''),
      isEditable: new UntypedFormControl(true)
    });

    this.customTextReplacements.push(formGroup);
    this.editCache = { rowId: id, data: formGroup.value };
  }

  removeTextReplacement(index: number) {
    this.customTextReplacements.removeAt(index);
  }

  ngOnDestroy(): void {
    if (this.unsubscribe$) {
      this.unsubscribe$.next(null);
      this.unsubscribe$.complete();
    }
  }
}
